import { Component, OnInit, Input, Output, EventEmitter, inject, HostListener, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Auth } from '@angular/fire/auth';
import { Firestore, collection, addDoc } from '@angular/fire/firestore';
import { Storage, ref, uploadBytes, getDownloadURL, deleteObject } from '@angular/fire/storage';
import { QuillModule } from 'ngx-quill';
import { AccessService } from '../services/access.service';
import { ConfigService } from '../services/config.service';

interface Attachment {
  name: string;
  url: string;
  storagePath?: string;
}

@Component({
  selector: 'app-email-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, QuillModule],
  templateUrl: './email-form.component.html',
  styleUrls: ['./email-form.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class EmailFormComponent implements OnInit {
  @Input() to: string = '';
  @Input() subject: string = '';
  @Input() body: string = '';
  @Input() attachments: Attachment[] = [];
  @Input() templateType?: string;
  /** KAN-147: the given attachments cannot be removed (e.g. an issued artist statement PDF). */
  @Input() fixedAttachments = false;
  @Output() emailSent = new EventEmitter<any>();
  @Output() close = new EventEmitter<void>();

  emailForm: FormGroup;
  isSending: boolean = false;
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private storage = inject(Storage);
  private accessService = inject(AccessService);
  private configService = inject(ConfigService);

  showEditor = true;

  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link', 'image']
    ]
  };

  resetEditor() {
    this.showEditor = false;
    setTimeout(() => this.showEditor = true, 0);
  }

  constructor(private fb: FormBuilder) {
    this.emailForm = this.fb.group({
      from: [{ value: '', disabled: true }],
      to: ['', Validators.required], 
      cc: [''],
      bcc: [''],
      subject: ['', Validators.required],
      body: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    const fromEmail = this.configService.getConfig().adminEmail;
    this.emailForm.patchValue({
      from: fromEmail,
      to: this.to,
      subject: this.subject,
      body: this.body
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: Event) {
    this.cancel();
  }

  openFirebaseStoragePicker(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = async (event: any) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        this.isSending = true;
        try {
          const user = this.auth.currentUser;
          const userId = user ? user.uid : 'anonymous';
          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (file.size > 20 * 1024 * 1024) { // 20MB limit to ensure it fits within Gmail's 25MB
              alert(`File ${file.name} is too large. Maximum size is 20MB.`);
              continue;
            }
            const storagePath = `users/${userId}/temp_attachments/${Date.now()}_${file.name}`;
            const storageRef = ref(this.storage, storagePath);
            const snapshot = await uploadBytes(storageRef, file);
            const url = await getDownloadURL(snapshot.ref);
            this.attachments.push({ name: file.name, url, storagePath });
          }
        } catch (error) {
          console.error('Error uploading attachment:', error);
          alert('Failed to upload attachment.');
        } finally {
          this.isSending = false;
        }
      }
    };
    input.click();
  }

  async removeAttachment(index: number) {
    const attachment = this.attachments[index];
    if (attachment.storagePath) {
      try {
        const fileRef = ref(this.storage, attachment.storagePath);
        await deleteObject(fileRef);
      } catch (error) {
        console.warn('Error deleting file from storage:', error);
      }
    }
    this.attachments.splice(index, 1);
  }

  cancel(): void {
    this.close.emit();
  }

  async sendEmail(): Promise<void> {
    if (this.emailForm.invalid) {
      return;
    }

    this.isSending = true;
    // Use getRawValue() to include the disabled 'from' field
    const formValues = this.emailForm.getRawValue();
    const currentUserEmail = this.auth.currentUser?.email || 'unknown';

    const htmlContent = formValues.body;
    const textContent = this.stripHtml(htmlContent);

    const emailData = {
      senderType: 'accounting',
      fromName: 'Accounting System',
      to: this.splitEmails(formValues.to),
      cc: this.splitEmails(formValues.cc),
      bcc: this.splitEmails(formValues.bcc),
      from: formValues.from,
      replyTo: formValues.from,
      subject: formValues.subject,
      message: {
        subject: formValues.subject,
        html: htmlContent,
        text: textContent
      },
      html: htmlContent,
      text: textContent,
      body: textContent,
      attachments: this.attachments.map(a => ({ name: a.name, url: a.url, storagePath: a.storagePath || '' })),
      sentAt: new Date(),
      sentBy: currentUserEmail
    };

    console.log('Sending email with data:', emailData);

    try {
      // Save receipt to Firestore
      const receipt = await addDoc(collection(this.firestore, 'email_receipts'), emailData);

      this.isSending = false;
      alert('Email sent successfully!');
      this.emailForm.reset();
      this.emailForm.patchValue({ from: this.configService.getConfig().adminEmail }); // Restore 'from' after reset
      this.attachments = [];
      // receiptId (KAN-147) lets a caller follow deliveryStatus; existing callers only read `to`
      this.emailSent.emit({ to: formValues.to, receiptId: receipt.id });
    } catch (error) {
      console.error('Error sending email:', error);
      this.isSending = false;
      alert('Failed to send email.');
    }
  }

  async saveTemplate() {
    const user = this.auth.currentUser;
    if (!user) return;

    const body = this.emailForm.get('body')?.value;
    if (!body) {
      alert('Message body is empty.');
      return;
    }

    try {
      const type = this.templateType || 'default';
      await this.accessService.saveEmailTemplate(user.uid, body, type);
      alert('Template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template.');
    }
  }

  private splitEmails(emails: string): string[] {
    if (!emails) return [];
    return emails.split(',').map(e => e.trim()).filter(e => e.length > 0);
  }

  private stripHtml(html: string): string {
    if (!html) return '';
    
    // 1. Replace block-level tags with newlines to preserve layout
    const text = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/<\/h[1-6]>/gi, '\n');

    // 2. Use DOMParser to strip tags and decode entities
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.body.textContent || '';
  }
}